import { DateTime } from 'luxon';
import { ChangeEvent, forwardRef, LegacyRef, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';

import { DatepickerProps } from 'components/generic-components/datepicker/type';
import CalendarIcon from 'icons/CalendarIcon';

import styles from 'components/generic-components/datepicker/Datepicker.module.css';

// Helper functions for holiday calculations
const getNthWeekdayOfMonth = (year: number, month: number, weekday: number, n: number): DateTime => {
  // Luxon weekday: 1=Mon ... 7=Sun
  let date = DateTime.utc(year, month, 1);
  while (date.weekday !== weekday) {
    date = date.plus({ days: 1 });
  }
  return date.plus({ weeks: n - 1 });
};

const getLastWeekdayOfMonth = (year: number, month: number, weekday: number): DateTime => {
  // Start from the last day of the month
  let date = DateTime.utc(year, month).endOf('month').startOf('day');
  while (date.weekday !== weekday) {
    date = date.minus({ days: 1 });
  }
  return date;
};

const getObservedDate = (date: DateTime): DateTime => {
  if (date.weekday === 6) return date.minus({ days: 1 }); // Saturday -> Friday
  if (date.weekday === 7) return date.plus({ days: 1 }); // Sunday -> Monday
  return date;
};

const calculateFederalHolidaysForYear = (year: number): string[] => {
  const holidays = [
    DateTime.utc(year, 1, 1), // New Year's Day
    getNthWeekdayOfMonth(year, 1, 1, 3), // Martin Luther King Jr. Day (3rd Mon in Jan)
    getNthWeekdayOfMonth(year, 2, 1, 3), // Presidents' Day (3rd Mon in Feb)
    getLastWeekdayOfMonth(year, 5, 1), // Memorial Day (Last Mon in May)
    DateTime.utc(year, 6, 19), // Juneteenth
    DateTime.utc(year, 7, 4), // Independence Day
    getNthWeekdayOfMonth(year, 9, 1, 1), // Labor Day (1st Mon in Sep)
    getNthWeekdayOfMonth(year, 10, 1, 2), // Columbus Day (2nd Mon in Oct)
    DateTime.utc(year, 11, 11), // Veterans Day
    getNthWeekdayOfMonth(year, 11, 4, 4), // Thanksgiving Day (4th Thu in Nov)
    DateTime.utc(year, 12, 25), // Christmas Day
  ];

  // Map to observed dates and return ISO strings
  return holidays.map((d) => getObservedDate(d).toISODate());
};

// Generate holidays for a range of years (e.g., current year - 1 to + 5 years)
const currentYear = new Date().getFullYear();
const holidayYears = Array.from({ length: 7 }, (_, i) => currentYear - 1 + i);

export const USBankHolidayDates = holidayYears.flatMap((year) => calculateFederalHolidaysForYear(year)).sort();

// Define an array of holidays (e.g., in the format 'YYYY-MM-DD')
export const USBankHolidays = USBankHolidayDates.map((date) => new Date(date));

export const holidaysInPTtmzLuxon = USBankHolidayDates.map((date) => DateTime.fromISO(date).toJSDate());

/* Function to check if a date is a weekend (Saturday or Sunday) */
/* export const isWeekday = (date: Date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6; // 0 is Sunday, 6 is Saturday
}; */
export const isWeekday = (date: Date) => {
  const dt = DateTime.fromJSDate(date).startOf('day').toLocal(); //, { zone: 'America/Los_Angeles' }
  const day = dt.weekday; // in Luxon, 1 is Monday and 7 is Sunday
  return day !== 6 && day !== 7; // 6 is Saturday, 7 is Sunday
};

const defaultMaxDate = new Date(`2100-01-01`);
const defaultMinDate = new Date(`1940-01-01`);

// Custom input component that adds slashes automatically
const DateInput = forwardRef(
  (
    { onChange, onClick, value, className, placeholder, required, disabled, ...props }: any,
    ref: LegacyRef<HTMLInputElement>,
  ) => {
    // Handle input change and auto-add slashes
    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
      let inputValue = e.target.value;

      // Allow only numbers and slashes
      inputValue = inputValue.replace(/[^\d\/]/g, '');

      // If user is deleting, just pass through
      if (inputValue.length < (value?.length || 0)) {
        const newEvent = {
          ...e,
          target: {
            ...e.target,
            value: inputValue,
          },
        };

        onChange(newEvent);
        return;
      }

      // Handle manually typed slashes and auto-formatting
      let rawValue = inputValue.replace(/\//g, ''); // Remove all slashes
      let formattedValue = '';

      // Format based on the number of digits we have
      if (rawValue.length > 0) {
        // First part - month (up to 2 digits)
        if (rawValue.length <= 2) {
          formattedValue = rawValue;
        }
        // Second part - add slash after month and format day
        else if (rawValue.length <= 4) {
          formattedValue = `${rawValue.substring(0, 2)}/${rawValue.substring(2)}`;
        }
        // Third part - add slashes after month and day and format year
        else {
          formattedValue = `${rawValue.substring(0, 2)}/${rawValue.substring(2, 4)}/${rawValue.substring(4)}`;
        }

        // Limit to 8 digits (MMDDYYYY)
        if (rawValue.length > 8) {
          rawValue = rawValue.substring(0, 8);
          formattedValue = `${rawValue.substring(0, 2)}/${rawValue.substring(2, 4)}/${rawValue.substring(4, 8)}`;
        }
      }

      // Now, if user typed a slash as their last character, respect that
      if (inputValue.endsWith('/') && !formattedValue.endsWith('/')) {
        // Two cases - either after month or after day
        if (formattedValue.indexOf('/') === -1 && rawValue.length === 2) {
          // User typed slash after month - maintain it
          formattedValue = `${formattedValue}/`;
        } else if (
          formattedValue.indexOf('/') !== -1 &&
          formattedValue.lastIndexOf('/') === 2 &&
          rawValue.length === 4
        ) {
          // User typed slash after day - maintain it
          formattedValue = `${formattedValue}/`;
        }
      }

      // If raw input ends with a slash and we already have a slash at the same position
      // in formatted value, avoid duplicate slashes
      if (inputValue.endsWith('/') && formattedValue.endsWith('/')) {
        const slashCount = (inputValue.match(/\//g) || []).length;
        const formattedSlashCount = (formattedValue.match(/\//g) || []).length;

        // If there would be too many slashes, remove the last one
        if (slashCount > formattedSlashCount) {
          formattedValue = formattedValue.substring(0, formattedValue.length - 1);
        }
      }

      // Create a synthetic event with the formatted value
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: formattedValue,
        },
      };

      onChange(newEvent);
    };

    return (
      <input
        ref={ref}
        onChange={handleInputChange}
        onClick={onClick}
        value={value}
        type="text"
        className={className}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        {...props}
      />
    );
  },
);

DateInput.displayName = 'DateInput';

const DatepickerComponent = forwardRef(
  (
    {
      preventOpenOnFocus,
      isMonthYearPicker,
      date,
      includeDates,
      disabledKeyboardNavigation,
      onChange,
      id,
      required,
      placeholder,
      withIcon,
      label,
      subLabel,
      isRange,
      showTimeSelect,
      register,
      name,
      disabled,
      minDate,
      notSetMinDate = false,
      maxDate,
      disableWeekends,
      disableHolidays,
      timeFormat = 'hh:mm aa',
      timeIntervals = 30,
      isTimePicker = false,
      selectsStart,
      selectsEnd,
      startDate: externalStartDate,
      endDate: externalEndDate,
      filterDate,
    }: DatepickerProps,
    ref: LegacyRef<HTMLInputElement>,
  ) => {
    const jsDate = date && !Array.isArray(date) && date?.isValid && !isRange ? date?.toJSDate() : undefined;
    const safeMinDate = minDate && !isNaN(minDate.getTime()) ? minDate : undefined;
    const safeMaxDate = maxDate && !isNaN(maxDate.getTime()) ? maxDate : undefined;
    const [dateRange, setDateRange] = useState(
      isRange && date?.[0]?.isValid ? [date?.[0]?.toJSDate(), date?.[1].toJSDate()] : [null, null],
    );
    const [startDate, endDate] = dateRange;

    // The time list is portaled to `#root-portal`, outside the modal's Radix scroll-lock
    // subtree. `react-remove-scroll` therefore cancels native wheel scrolling on it (only
    // dragging the scrollbar survives). Manually scroll the list on wheel so the mouse wheel
    // works inside modals. We preventDefault ourselves so this never double-scrolls when the
    // picker is used outside a scroll-locked modal.
    useEffect(() => {
      if (!isTimePicker) return;
      const onWheel = (event: WheelEvent) => {
        const target = event.target as HTMLElement | null;
        const list = target?.closest?.('.react-datepicker__time-list') as HTMLElement | null;
        if (!list) return;
        event.preventDefault();
        list.scrollTop += event.deltaY;
      };
      document.addEventListener('wheel', onWheel, { passive: false, capture: true });
      return () => document.removeEventListener('wheel', onWheel, { capture: true });
    }, [isTimePicker]);

    useEffect(() => {
      isRange && startDate && endDate && onChange?.([startDate, endDate] as any);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    const getDateFormat = () => {
      if (isTimePicker) return timeFormat;
      if (isMonthYearPicker) return 'MM/yyyy';
      if (showTimeSelect) return `MM/dd/yyyy ${timeFormat}`;
      return 'MM/dd/yyyy';
    };

    const handleMonthChange = (date: Date) => {
      if (jsDate) {
        // Maintain the same day in the new month
        const newDate = new Date(date);
        newDate.setDate(jsDate.getDate());

        const currentMonth = newDate.getMonth();
        if (currentMonth !== date.getMonth()) {
          // Set to last day of the previous month
          newDate.setDate(0);
        }

        // Ensure the date doesn't go beyond min/max boundaries
        const minDateValue = safeMinDate || defaultMinDate;
        const maxDateValue = safeMaxDate || defaultMaxDate;

        if (newDate < minDateValue) {
          newDate.setTime(minDateValue.getTime());
        } else if (newDate > maxDateValue) {
          newDate.setTime(maxDateValue.getTime());
        }

        onChange(DateTime.fromJSDate(newDate));
      }
    };

    const handleYearChange = (date: Date) => {
      if (jsDate) {
        // Maintain the same month and day in the new year
        const newDate = new Date(date);
        newDate.setMonth(jsDate.getMonth());
        newDate.setDate(jsDate.getDate());

        // Handle February 29 in non-leap years
        if (newDate.getMonth() !== jsDate.getMonth()) {
          newDate.setDate(0); // Set to last day of previous month
        }

        // Ensure the date doesn't go beyond min/max boundaries
        const minDateValue = safeMinDate || defaultMinDate;
        const maxDateValue = safeMaxDate || defaultMaxDate;

        if (newDate < minDateValue) {
          newDate.setTime(minDateValue.getTime());
        } else if (newDate > maxDateValue) {
          newDate.setTime(maxDateValue.getTime());
        }

        onChange(DateTime.fromJSDate(newDate));
      }
    };

    return (
      <>
        {label &&
          (typeof label === 'string' ? (
            <div className="form-label flex">
              <div className="flex items-center justify-between">
                <label htmlFor={id} className={`${required ? 'required' : ''}`}>
                  {label}
                </label>
                <span className="text-xs text-neutral-400">
                  {' '}
                  {isTimePicker ? timeFormat : isMonthYearPicker ? 'MM/YYYY' : 'MM/DD/YYYY'}
                </span>
              </div>
              {subLabel && (
                <label htmlFor={id} className="block text-xs text-neutral-500">
                  {subLabel}
                </label>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              {label}{' '}
              <span className="text-xs text-neutral-500">
                {' '}
                {isTimePicker ? timeFormat : isMonthYearPicker ? 'MM/YYYY' : 'MM/DD/YYYY'}
              </span>
            </div>
          ))}

        <div className="date-selector relative">
          {isRange ? (
            <DatePicker
              dateFormat={getDateFormat()}
              calendarClassName="min-h-[302px]" // this stops the movement of the DatePicker component when the number of weeks change in the view
              selectsRange={true}
              id={id}
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => setDateRange(update)}
              placeholderText={placeholder}
              required={required}
              showTimeSelect={showTimeSelect}
              autoComplete="off"
              disabled={disabled}
              customInput={
                <DateInput
                  ref={ref}
                  {...(register && { ...register(name) })}
                  className={`form-input ${withIcon ? 'datePicker-withIcon' : ''}`}
                />
              }
              portalId="root-portal"
              timeFormat={timeFormat}
              timeIntervals={timeIntervals}
              minDate={safeMinDate || defaultMinDate}
              maxDate={safeMaxDate || defaultMaxDate}
            />
          ) : isMonthYearPicker ? (
            <DatePicker
              preventOpenOnFocus={preventOpenOnFocus}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              id={id}
              selected={jsDate || (!notSetMinDate && safeMinDate)}
              onChange={(e) => onChange(DateTime.fromJSDate(e))}
              placeholderText={placeholder}
              required={required}
              autoComplete="off"
              disabled={disabled}
              minDate={safeMinDate || defaultMinDate}
              maxDate={safeMaxDate || defaultMaxDate}
              disabledKeyboardNavigation={disabledKeyboardNavigation}
              customInput={
                <DateInput
                  ref={ref}
                  {...(register && { ...register(name) })}
                  className={`form-input ${withIcon ? 'datePicker-withIcon' : ''}`}
                />
              }
              portalId="root-portal"
            />
          ) : (
            <DatePicker
              dateFormat={getDateFormat()}
              calendarClassName={isTimePicker ? 'min-h-fit' : 'min-h-[324px]'} // this stops the movement of the DatePicker component when the number of weeks change in the view
              popperClassName={isTimePicker ? styles.timePicker : undefined}
              id={id}
              timeFormat={timeFormat}
              timeIntervals={timeIntervals}
              selected={jsDate || (!notSetMinDate && safeMinDate)}
              onChange={(e) => onChange(DateTime.fromJSDate(e))}
              onMonthChange={handleMonthChange}
              onYearChange={handleYearChange}
              onChangeRaw={(event) => {
                // For raw text input, only trigger date selection if input is a full date
                const value = event.target.value;
                if (value?.length === 10 && value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                  // Valid date format MM/DD/YYYY
                  const parts = value.split('/');
                  const month = parseInt(parts[0], 10) - 1; // Months are 0-indexed in JS Date
                  const day = parseInt(parts[1]);
                  const year = parseInt(parts[2]);

                  // Only process if date components are valid
                  if (
                    !isNaN(month) &&
                    !isNaN(day) &&
                    !isNaN(year) &&
                    month >= 0 &&
                    month < 12 &&
                    day > 0 &&
                    day <= 31
                  ) {
                    const date = new Date(year, month, day);

                    // Check if date is within allowed range
                    const minAllowed = !isTimePicker ? safeMinDate || defaultMinDate : undefined;
                    const maxAllowed = !isTimePicker ? safeMaxDate || defaultMaxDate : undefined;

                    // Only update if date is within allowed range
                    if ((!minAllowed || date >= minAllowed) && (!maxAllowed || date <= maxAllowed)) {
                      onChange(DateTime.fromJSDate(date));
                    }
                  }
                }
              }}
              placeholderText={placeholder}
              required={required}
              showTimeSelect={showTimeSelect || isTimePicker}
              showTimeSelectOnly={isTimePicker}
              autoComplete="off"
              disabled={disabled}
              showMonthDropdown={!isTimePicker}
              showYearDropdown={!isTimePicker}
              scrollableYearDropdown={!isTimePicker}
              yearDropdownItemNumber={100}
              minDate={!isTimePicker ? safeMinDate || defaultMinDate : undefined}
              maxDate={!isTimePicker ? safeMaxDate || defaultMaxDate : undefined}
              // excludeDates={USBankHolidays}
              {...(disableWeekends && { filterDate: isWeekday })}
              // {...(disableHolidays && { filterDate: (date) => USBankHolidays.includes(date) })}
              {...(disableHolidays && { excludeDates: holidaysInPTtmzLuxon })}
              // filterDate={isWeekday}
              {...(filterDate && { filterDate })}
              includeDates={includeDates && includeDates.length > 0 && [...includeDates]}
              disabledKeyboardNavigation={disabledKeyboardNavigation}
              selectsStart={selectsStart}
              selectsEnd={selectsEnd}
              startDate={externalStartDate}
              endDate={externalEndDate}
              customInput={
                <DateInput
                  ref={ref}
                  {...(register && { ...register(name) })}
                  className={`form-input ${withIcon ? 'datePicker-withIcon' : ''}`}
                />
              }
              portalId="root-portal"
            />
          )}
          {withIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <CalendarIcon />
            </div>
          )}
        </div>
      </>
    );
  },
);

DatepickerComponent.displayName = 'DatepickerComponent';

export default DatepickerComponent;
